import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import api from '../../services/api';

const PaymentSuccess = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPaymentStatus = async () => {
      try {
        setLoading(true);

        // Get basic payment info
        const response = await api.get(`/v1/payments/${token}/`);
        setPaymentData({
          method: 'stripe',
          customer: response.data.customer,
          booking: response.data.booking,
          amount: response.data.amount,
          organization: response.data.organization || {}
        });
      } catch (err) {
        setError('Failed to load payment information');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchPaymentStatus();
    }
  }, [token]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="50vh">
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body1">Verifying payment status...</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  const organizationName = paymentData?.organization?.name || 'AutoHub Autocentres';
  const organizationLogo = paymentData?.organization?.logo_url || '';
  const organizationPhone = paymentData?.organization?.phone || '';
  const organizationLocation = paymentData?.organization?.location || '';
  const organizationAddress = paymentData?.organization?.address || '';
  const contactAddress = organizationLocation || organizationAddress || '';

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 0, overflow: 'hidden' }}>
        {/* Top brand bar */}
        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          {organizationLogo ? (
            <img
              src={organizationLogo}
              alt={organizationName}
              style={{ height: 36, width: 'auto', display: 'block' }}
            />
          ) : (
            <Typography variant="h6">{organizationName}</Typography>
          )}
        </Box>

        <Box sx={{ p: 4 }}>
          {/* Success Card */}
          <Card sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Payment Complete!
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto', mb: 2 }}>
                Thank you for your payment. We truly appreciate your business and are grateful for the trust
                you've placed in us. Your transaction has been processed successfully, and a confirmation
                email has been sent to your registered email address.
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                If you have any questions or need assistance, please don't hesitate to contact us:
              </Typography>
            </CardContent>
          </Card>

          {/* Contact Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Our Address
                  </Typography>
                  <Typography variant="body2">
                    {contactAddress || 'Address not provided'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Contact Number
                  </Typography>
                  <Typography variant="body2">
                    {organizationPhone || 'Phone not provided'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            We look forward to serving you again soon. Thank you for choosing {organizationName}!
          </Typography>
        </Box>

        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            © {new Date().getFullYear()} {organizationName}. All rights reserved.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default PaymentSuccess;
